import * as THREE from 'three';
import { OrbitControls } from '../libs/OrbitControls';
import { resizeRendererToDisplaySize } from '../libs/three-utils';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { ACESFilmicToneMapping, AnimationClip, AnimationMixer, BufferGeometry, Color, DataTextureLoader, DepthTexture, Float32BufferAttribute, InterpolateSmooth, LinearFilter, LinearMipMapLinearFilter, LinearToneMapping, LoadingManager, Mesh, MeshBasicMaterial, Object3D, PlaneGeometry, Raycaster, ShaderMaterial, sRGBEncoding, TextureLoader, Vector2, Vector3, VectorKeyframeTrack, WebGLRenderTarget } from 'three';
import { fromEvent, tap } from 'rxjs';
import { SecondOrderSystemAngle } from './utils/second-order-angle';
import anime from 'animejs/lib/anime.es.js';
import * as HammerImport from 'hammerjs/hammer';
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js';


import plateVert from './shader/plate.vert.glsl';
import plateFrag from './shader/plate.frag.glsl';
import groundVert from './shader/ground.vert.glsl';
import groundFrag from './shader/ground.frag.glsl';
import compositeVert from './shader/composite.vert.glsl';
import compositeFrag from './shader/composite.frag.glsl';
import dofVert from './shader/dof.vert.glsl';
import dofFrag from './shader/dof.frag.glsl';

// the target duration of one frame in milliseconds
const TARGET_FRAME_DURATION = 16;

// total time
let time = 0;

// duration betweent the previous and the current animation frame
let deltaTime = 0;

// total framecount according to the target frame duration
let frames = 0;

// relative frames according to the target frame duration (1 = 60 fps)
// gets smaller with higher framerates --> use to adapt animation timing
let deltaFrames = 0;

const settings = {
}

const PLATE_COUNT = 4;

// module variables
let _isDev,
    _pane,
    camera,
    scene,
    renderer,
    controls,
    plateMeshes,
    plateMixers,
    plateAActions,
    carouselGroup,
    carouselItemsGroup,
    groundMesh,
    isPointerDown,
    selectedItemIndex;
let depthRT, layer1RT, layer2RT, layer3RT, layer4RT, dof1RT, quadMesh;
const pointerPos = new THREE.Vector2(0, 0);
const pointerDownPos = new THREE.Vector2(0, 0);
const pointerDownRotation = new THREE.Vector3();

const carouselAngle = new SecondOrderSystemAngle(1,  0.8, 1, 0);
const plateAngle = new SecondOrderSystemAngle(0.7,  0.9, 1, 0);
let thetaOffset = 0;

let selectionTimeline;

// html elements
const itemTitleElms = Array.from(document.body.querySelectorAll('.item-titel'));
const itemContentElms = Array.from(document.body.querySelectorAll('.item-content'));

let planeTexture, envTexture, hdrCubeRenderTarget;

let hammertime;

const depthMaterial = new MeshBasicMaterial({color: 0, depthTest: true, depthWrite: true});

let copyMaterial, dofMaterial;


const queryString = window.location.search;
const urlParams = new URLSearchParams(queryString);
const highQuality = urlParams.get('hd') === '1';

if (highQuality) console.info('high quality version');

const raycaster = new Raycaster();

const rands = (new Array(PLATE_COUNT)).fill(0).map(() => Math.random());

const viewportSize = new Vector2();

async function init(canvas, onInit = null, isDev = false, pane = null, theme = null) {
    _isDev = isDev;
    _pane = pane;

    camera = new THREE.PerspectiveCamera( 45, window.innerWidth / window.innerHeight, .8, 6 );
    camera.position.z = 3.1;
    camera.position.y = .9;
    camera.position.x = 0;
    camera.lookAt(new THREE.Vector3(0, -.5, 0));

    scene = new THREE.Scene();
    scene.background = new Color('#fff');
    renderer = new THREE.WebGLRenderer( { canvas, antialias: true, alpha: true } );
    renderer.sortObjects = false;
    renderer.autoClearColor = false;
    document.body.appendChild( renderer.domElement );

    const manager = new LoadingManager();
    const normalMaps = new Array(PLATE_COUNT);
    const heightMaps = new Array(PLATE_COUNT);
    for(let i=0; i<PLATE_COUNT; ++i) { 
        const normalLoader = new TextureLoader(manager);
        const normalMapUrl = theme === 'lisec' ? new URL(`../assets/lisec_normal-map-0${i}.png`, import.meta.url) : new URL(`../assets/normal-map-0${i}.png`, import.meta.url);
        normalLoader.load(normalMapUrl, (tex) => normalMaps[i] = (tex));
        const heightLoader = new TextureLoader(manager);
        const heightMapUrl = theme === 'lisec' ? new URL(`../assets/lisec_height-map-0${i}.png`, import.meta.url) : new URL(`../assets/height-map-0${i}.png`, import.meta.url);
        heightLoader.load(heightMapUrl, (tex) => heightMaps[i] = (tex));
    }

    const hdrEquirect = new RGBELoader(manager)
        .setDataType(THREE.HalfFloatType).load(
        new URL('../assets/env-5.hdr', import.meta.url),  
        (hdr) => { 
          envTexture = hdr;
        }, undefined, (e) => console.log(e)
      );

    /*const envLoader = new TextureLoader(manager);
    envLoader.load(new URL(`../assets/env-5.jpg`, import.meta.url), (tex) => {
        envTexture = tex;
        envTexture.minFilter = THREE.LinearMipMapLinearFilter;
        envTexture.generateMipmaps = true;
        envTexture.magFilter = THREE.LinearFilter
        envTexture.needsUpdate = true;
    });*/

    const planeLoader = new TextureLoader(manager);
    planeLoader.load(theme === 'lisec' ? new URL(`../assets/lisec-plane.png`, import.meta.url) : new URL(`../assets/plane.png`, import.meta.url), (tex) => {
        planeTexture = tex;
        planeTexture.wrapS = THREE.ClampToEdgeWrapping;
        planeTexture.wrapT = THREE.ClampToEdgeWrapping;
        planeTexture.needsUpdate = true;
    });

    await new Promise((resolve) => {
        manager.onLoad = function ( ) {
            resolve();
        }
    });

    //controls = new OrbitControls( camera, renderer.domElement );
    //controls.update();


    const size = renderer.getSize(new Vector2());
    layer1RT = new WebGLRenderTarget(size.x, size.y, { samples: 0, depthBuffer: false, generateMipmaps: true, magFilter: THREE.LinearFilter, minFilter: THREE.LinearMipMapLinearFilter });
    layer2RT = new WebGLRenderTarget(size.x, size.y, { samples: highQuality ? 2 : 0, depthBuffer: false, generateMipmaps: true, magFilter: THREE.LinearFilter, minFilter: THREE.LinearMipMapLinearFilter });
    layer3RT = new WebGLRenderTarget(size.x, size.y, { samples: highQuality ? 8 : 0, depthBuffer: false,generateMipmaps: true, magFilter: THREE.LinearFilter, minFilter: THREE.LinearMipMapLinearFilter });
    layer4RT = new WebGLRenderTarget(size.x, size.y, { samples: highQuality ? renderer.capabilities.maxSamples : 0, depthBuffer: false });
    depthRT = new WebGLRenderTarget(size.x, size.y);
    dof1RT = new WebGLRenderTarget(size.x, size.y, { depthBuffer: false });
    depthRT.depthBuffer = true;
    depthRT.depthTexture = new DepthTexture();
    depthRT.depthTexture.format = THREE.DepthFormat;
    depthRT.depthTexture.type = THREE.FloatType;

    const loader = new GLTFLoader();
    const plateScenePromise = new Promise(resolve =>
        loader.load((theme === 'lisec' ? new URL('../assets/lisec-plate.glb', import.meta.url) : new URL('../assets/plate.glb', import.meta.url)).toString(), glb => resolve(glb))
    );
    const plateScene = await plateScenePromise;
    const plateMesh = plateScene.scene.children[0];
    plateMesh.scale.multiplyScalar(0.85);
    const material = new THREE.ShaderMaterial( {
        uniforms: {
            uNormalMap: { value: null },
            uHeightMap: { value: null },
            uBackgroundTexture: { value: null },
            uEnvMap: { value: envTexture },
            uTime: { value: 1.0 },
            uSelectProgress: { value: 0 },
		    uResolution: { value: new THREE.Vector2() }
        },
        vertexShader: plateVert,
        fragmentShader: plateFrag,
        glslVersion: THREE.GLSL3
    });
    plateMesh.material = material;
    plateMesh.onBeforeRender = () => {
        plateMesh.material.uniforms.uTime.value = time;
    }

    carouselGroup = new THREE.Object3D();
    carouselGroup.frustumCulled = false;
    carouselItemsGroup = new THREE.Object3D();
    carouselGroup.add(carouselItemsGroup);
    carouselGroup.position.y += 0.15;
    carouselGroup.position.x -= 0.5;
    plateMeshes = [];
    plateMixers = [];
    plateAActions = [];
    selectItem(0);
    for(let i=0; i<PLATE_COUNT; ++i) {
        const mesh = plateMesh.clone();
        mesh.material = plateMesh.material.clone();
        mesh.material.uniforms.uNormalMap.value = normalMaps[i];
        mesh.material.uniforms.uHeightMap.value = heightMaps[i];
        const meshContainer = new Object3D();
        meshContainer.add(mesh);
        plateMeshes.push(meshContainer);

        const radius = 1.25;
        const phi = ((Math.PI * 2) / PLATE_COUNT) * i;
        meshContainer.position.x = radius * Math.cos(phi);
        meshContainer.position.z = radius * Math.sin(phi);

        carouselItemsGroup.add(meshContainer);
    }


    const groundGeo = new PlaneGeometry(6, 6, 1, 1);
    const groundMaterial = new ShaderMaterial( {
        uniforms: {
            uCircleTexture: { value: planeTexture }
        },
        vertexShader: groundVert,
        fragmentShader: groundFrag,
        glslVersion: THREE.GLSL3,
        depthWrite: true
    });
    groundMesh = new Mesh(groundGeo, groundMaterial);
    groundMesh.rotation.x = -Math.PI / 2;
    groundMesh.position.y = -0.44;
    groundMesh.frustumCulled = false;
    carouselGroup.add(groundMesh);

    scene.add( carouselGroup );

    copyMaterial = new ShaderMaterial( {
        uniforms: {
            uColorTexture: { value: null }
        },
        vertexShader: compositeVert,
        fragmentShader: compositeFrag,
        glslVersion: THREE.GLSL3,
        depthTest: false,
        depthWrite: false
    });

    dofMaterial = new ShaderMaterial( {
        uniforms: {
            uColorTexture: { value: null },
            uDepthTexture: { value: null },
            uCameraNear: { value: camera.near },
            uCameraFar: { value: camera.far },
            uInverseProjectionMatrix: { value: camera.projectionMatrixInverse },
            uBlurDirection: { value: new Vector2(1, 0) }
        },
        vertexShader: dofVert,
        fragmentShader: dofFrag,
        glslVersion: THREE.GLSL3,
        depthTest: false,
        depthWrite: false
    });

    const quadGeo = new BufferGeometry();
    quadGeo.setAttribute( 'position', new Float32BufferAttribute( [ - 1, 3, 0, - 1, - 1, 0, 3, - 1, 0 ], 3 ) );
    quadGeo.setAttribute( 'uv', new Float32BufferAttribute( [ 0, 2, 0, 0, 2, 0 ], 2 ) );
    quadMesh = new Mesh(quadGeo, copyMaterial);

    Hammer = window.Hammer;
    hammertime = new Hammer(renderer.domElement);
    hammertime.get('swipe').set({ direction: Hammer.DIRECTION_ALL, velocity: 0.1, threshold: 10 });
    hammertime.get('tap').set({ time: 1000 });
    hammertime.on('swipe', (e) => {
        if (selectionTimeline) return;


        let newIndex;
        if (e.direction === 4) {
            newIndex = (selectedItemIndex + 1) % PLATE_COUNT;

            selectItem(newIndex);
        } else if (e.direction === 2){
            newIndex = selectedItemIndex - 1;
            newIndex = newIndex < 0 ? PLATE_COUNT - 1 : newIndex;

            selectItem(newIndex);
        }
    });
    hammertime.on('swipe', (e) => {
        if (e.direction === 8 && !selectionTimeline) {
            toggleItem();
        } else if (e.direction === 16 && selectionTimeline) { 
            toggleItem();
        }
    });
    hammertime.on('tap', (e) => {
        const pointer = new Vector2();
        pointer.x = ( e.center.x / window.innerWidth ) * 2 - 1;
	    pointer.y = - ( e.center.y / window.innerHeight ) * 2 + 1;

        // update the picking ray with the camera and pointer position
        raycaster.setFromCamera( pointer, camera );
        // calculate objects intersecting the picking ray
        const intersects = raycaster.intersectObjects( scene.children );
        const selectedMesh = plateMeshes[selectedItemIndex];
        const itemTapped = intersects.some(i => i.object === selectedMesh.children[0]);
        const tappedItemIndex = plateMeshes.findIndex((g, ndx) => intersects.some(i => i.object === g.children[0]));

        //if((!selectionTimeline && itemTapped) || (selectionTimeline && !itemTapped)) {
        if (itemTapped || selectionTimeline) {
            toggleItem();
        } else if (tappedItemIndex !== null && tappedItemIndex !== undefined && tappedItemIndex !== -1) {
            selectItem(tappedItemIndex);
        }
    });

    fromEvent(renderer.domElement, 'pointerdown').subscribe((e) => {
        isPointerDown = true;
        pointerDownRotation.copy(carouselGroup.rotation);
        pointerDownPos.set(e.clientX, e.clientY);
        pointerPos.copy(pointerDownPos);
    });
    fromEvent(renderer.domElement, 'pointermove').subscribe((e) => {
        if (isPointerDown) {
            pointerPos.set(e.clientX, e.clientY);
        }
    });
    fromEvent(renderer.domElement, 'pointerup').subscribe((e) => {
        isPointerDown = false;
    });

    fromEvent(window, 'click').subscribe(async (e) => {
        //toggleItem();
    });

    if (onInit) onInit(this);
    
    resize();
}

function selectItem(index) {
    if (selectedItemIndex !== undefined) {
        itemTitleElms[selectedItemIndex].style.transition = 'transform 100ms cubic-bezier(0.895, 0.030, 0.685, 0.220)';
        itemTitleElms[selectedItemIndex].style.transform = 'translateX(100%)';
    }

    if (index >= 0) {
        selectedItemIndex = index;
        itemTitleElms[index].style.transition = 'transform 500ms cubic-bezier(0.055, 0.975, 0.050, 1.000) 600ms';
        itemTitleElms[index].style.transform = 'translateX(0)';
    }
}

async function toggleItem() {
    const selectedMesh = plateMeshes[selectedItemIndex];

    if (selectionTimeline) {

        if(!selectionTimeline.completed) return;

        itemContentElms[selectedItemIndex].style.transition = 'opacity 300ms';
        itemContentElms[selectedItemIndex].style.opacity = 0;

        selectItem(selectedItemIndex);
        selectionTimeline.reverse();
        selectionTimeline.play();
        await selectionTimeline.finished;
        selectionTimeline = null;
    } else {
        selectItem(-1);

        itemContentElms[selectedItemIndex].style.transition = 'opacity 300ms 800ms';
        itemContentElms[selectedItemIndex].style.opacity = 1;

        const totalDuration = 1300;

        selectionTimeline = anime.timeline({
            easing: 'easeInOutExpo',
            duration: totalDuration,
            autoplay: false,
            update: (e) => {
                selectedMesh.children[0].material.uniforms.uSelectProgress.value = e.progress / 100;
            }
        });

        anime({
            targets: selectedMesh.children[0].position,
            z: [
                { value: -0.3, duration: 300, easing: 'easeOutQuint' },
                { value: 0, duration: 400, easing: 'easeInOutQuint' },
            ],
            duration: 200
        });

        const radius = 2;
        const phi = ((Math.PI * 2) / PLATE_COUNT) * selectedItemIndex;
        selectionTimeline.add({
            targets: selectedMesh.position,
            x: radius * Math.cos(phi),
            y: 1.9,
            z:  radius * Math.sin(phi)
        });
        selectionTimeline.add({
            targets: selectedMesh.scale,
            x: 2.4,
            y: 2.4,
        }, 0);
        selectionTimeline.add({
            targets: selectedMesh.children[0].rotation,
            y: Math.PI,
            duration: totalDuration * 0.8
        }, totalDuration * 0.1);

        selectionTimeline.add({
            targets: carouselGroup.position,
            y: -1.5,
            x: -0.18
        }, 0);

        selectionTimeline.add({
            targets: camera.rotation,
            x: 0
        }, 0);

        selectionTimeline.play();
    }
}

function run(t = 0) {
    deltaTime = Math.max(1, Math.min(TARGET_FRAME_DURATION, t - time));
    time = t;
    deltaFrames = deltaTime / TARGET_FRAME_DURATION;
    frames += deltaFrames;

    animate();
    render();

    requestAnimationFrame((t) => run(t));
}

function resize() {
    console.log(renderer);
    if (renderer && resizeRendererToDisplaySize(renderer)) {
        const canvas = renderer.domElement;
        camera.aspect = canvas.clientWidth / canvas.clientHeight;
        camera.updateProjectionMatrix();

        dofMaterial.uniforms.uInverseProjectionMatrix.value = camera.projectionMatrixInverse;

        const size = renderer.getSize(viewportSize);
        depthRT.setSize(size.x, size.y);
        layer1RT.setSize(size.x, size.y);
        layer2RT.setSize(size.x, size.y);
        layer3RT.setSize(size.x, size.y);
        layer4RT.setSize(size.x, size.y);
        dof1RT.setSize(size.x, size.y);
    }
}

function animate() {
    //controls.update();

    const selectedMesh = plateMeshes[selectedItemIndex];

    if (isPointerDown && !selectionTimeline) {
        const offset = ((pointerPos.x - pointerDownPos.x) / viewportSize.x) * 4;
        thetaOffset = offset;
    } else {
        thetaOffset *= 0.8;
    }

    const dt = (deltaTime + 1) * 0.001;
    const theta = Math.atan2(-selectedMesh.position.x, selectedMesh.position.z) + thetaOffset;
    carouselAngle.update(dt, theta + 0.16);
    plateAngle.update(dt, -theta);
    carouselGroup.rotation.y = carouselAngle.value;


    for(let i=0; i<PLATE_COUNT; ++i) {
        const mesh = plateMeshes[i];
        mesh.rotation.y = plateAngle.value;

        mesh.children[0].position.y = 0.02 * Math.sin(time * 0.0006 + rands[i]);
        if (selectionTimeline)
            mesh.children[0].position.y *= 1 - selectionTimeline.progress / 100; 
    }
}

function blitColor(source, target) {
    const sourceRenderTargetProperties = renderer.properties.get( source );
    const targetRenderTargetProperties = renderer.properties.get( target );
    const gl = renderer.getContext();

	if ( sourceRenderTargetProperties.__webglFramebuffer !== undefined && targetRenderTargetProperties.__webglFramebuffer !== undefined ) {
        gl.bindFramebuffer(gl.READ_FRAMEBUFFER, sourceRenderTargetProperties.__webglFramebuffer);
        gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, targetRenderTargetProperties.__webglFramebuffer);
        gl.clearBufferfv(gl.COLOR, 0, [0.0, 0.0, 0.0, 1.0]);
        gl.blitFramebuffer(0, 0, source.width, source.height, 0, 0, target.width, target.height, gl.COLOR_BUFFER_BIT, gl.LINEAR);
    }
}

function render() {
    
    plateMeshes[0].children[0].visible = true;
    plateMeshes[1].children[0].visible = true;
    plateMeshes[2].children[0].visible = true;
    plateMeshes[3].children[0].visible = true;
    scene.overrideMaterial = depthMaterial;
    groundMesh.visible = true;
    renderer.setRenderTarget(depthRT);
    renderer.clear();
    renderer.render( scene, camera );
    scene.overrideMaterial = null;

    // render the ground plane
    renderer.setRenderTarget(layer1RT);
    carouselItemsGroup.visible = false;
    groundMesh.visible = true;
    renderer.alpha = false;
    renderer.clearColor(1, 1, 1, 1);
    renderer.render( scene, camera );
    groundMesh.visible = false;
    renderer.alpha = true;
    carouselItemsGroup.visible = true;

    // sort the plate meshes by their z position
    const sortedMeshes = [];
    for(let i=0; i<PLATE_COUNT; ++i) {
        const mesh = plateMeshes[i];
        mesh.visible = true;
        const pos = mesh.getWorldPosition(new Vector3());
        pos.applyMatrix4(camera.matrixWorldInverse);
        sortedMeshes.push({mesh: mesh.children[0], z: pos.z});
    }
    sortedMeshes.sort((a, b) => a.z - b.z);

    if (highQuality) {
        renderer.setRenderTarget(layer2RT);
        copyMaterial.uniforms.uColorTexture.value = layer1RT.texture;
        renderer.render(quadMesh, camera);
    } else {
        blitColor(layer1RT, layer2RT);
        renderer.setRenderTarget(layer2RT);
    }
    sortedMeshes[0].mesh.visible = true;
    sortedMeshes[1].mesh.visible = false;
    sortedMeshes[2].mesh.visible = false;
    sortedMeshes[3].mesh.visible = false;
    for(let i=0; i<PLATE_COUNT; ++i) {
        const mesh = sortedMeshes[i].mesh;
        renderer.getSize(mesh.material.uniforms.uResolution.value);
        mesh.material.uniforms.uBackgroundTexture.value = layer1RT.texture;
    }
    renderer.render( scene, camera );

    if (highQuality) {
        renderer.setRenderTarget(layer3RT);
        copyMaterial.uniforms.uColorTexture.value = layer2RT.texture;
        renderer.render(quadMesh, camera);
    } else {
        blitColor(layer2RT, layer3RT);
        renderer.setRenderTarget(layer3RT);
    }
    sortedMeshes[0].mesh.visible = false;
    sortedMeshes[1].mesh.visible = true;
    sortedMeshes[2].mesh.visible = true;
    sortedMeshes[3].mesh.visible = false;
    for(let i=0; i<PLATE_COUNT; ++i) {
        const mesh = sortedMeshes[i].mesh;
        renderer.getSize(mesh.material.uniforms.uResolution.value);
        mesh.material.uniforms.uBackgroundTexture.value = layer2RT.texture;
    }
    renderer.render( scene, camera );

    if (highQuality) {
        renderer.setRenderTarget(layer4RT);
        copyMaterial.uniforms.uColorTexture.value = layer3RT.texture;
        renderer.render(quadMesh, camera);
    } else {
        blitColor(layer3RT, layer4RT);
        renderer.setRenderTarget(layer4RT);
    }
    sortedMeshes[0].mesh.visible = false;
    sortedMeshes[1].mesh.visible = false;
    sortedMeshes[2].mesh.visible = false;
    sortedMeshes[3].mesh.visible = true;
    for(let i=0; i<PLATE_COUNT; ++i) {
        const mesh = sortedMeshes[i].mesh;
        renderer.getSize(mesh.material.uniforms.uResolution.value);
        mesh.material.uniforms.uBackgroundTexture.value = layer3RT.texture;
    }
    renderer.render( scene, camera );

    /*renderer.setRenderTarget(null);
    quadMesh.material.uniforms.uColorTexture.value = layer2RT.texture;
    renderer.render(quadMesh, camera);*/

    renderer.setRenderTarget(dof1RT);
    quadMesh.material = dofMaterial;
    dofMaterial.uniforms.uBlurDirection.value.set(1, 0);
    dofMaterial.uniforms.uColorTexture.value = layer4RT.texture;
    dofMaterial.uniforms.uDepthTexture.value = depthRT.depthTexture;
    renderer.render(quadMesh, camera);
    renderer.setRenderTarget(null);
    dofMaterial.uniforms.uBlurDirection.value.set(0, 1);
    dofMaterial.uniforms.uColorTexture.value = dof1RT.texture;
    dofMaterial.uniforms.uDepthTexture.value = depthRT.depthTexture;
    renderer.render(quadMesh, camera);
    quadMesh.material = copyMaterial;
}

export default {
    init,
    run,
    resize
}
